/**
 * TranslationPicker — inline language multi-select for the Presenter
 * Console. Lets the operator toggle target languages that captions will
 * be translated into (broadcast to output windows via BroadcastChannel).
 */
import React from 'react';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { Globe2, X } from 'lucide-react';
import { getToken } from '@/lib/authToken';

const API = process.env.REACT_APP_BACKEND_URL;
const authH = () => ({ Authorization: `Bearer ${getToken()}` });

export default function TranslationPicker({ value = [], onChange }) {
  const [open, setOpen] = React.useState(false);
  const langsQ = useQuery({
    queryKey: ['worship-caption-langs'],
    queryFn: async () => (await axios.get(
      `${API}/api/admin/worship/captions/languages`, { headers: authH() },
    )).data,
  });

  const languages = langsQ.data?.languages || [];
  const selected = new Set(value);
  const toggle = (code) => {
    const next = new Set(selected);
    if (next.has(code)) next.delete(code); else next.add(code);
    onChange?.(Array.from(next));
  };

  const summary = value.length === 0
    ? 'no translations'
    : value.length <= 3
      ? value.join(' · ')
      : `${value.slice(0, 2).join(' · ')} +${value.length - 2}`;

  return (
    <div className="relative flex items-center gap-1">
      <button
        type="button"
        data-testid="btn-translate"
        onClick={() => setOpen((v) => !v)}
        className={`flex items-center gap-1 rounded-full border px-2 py-1 text-[10px] ${
          value.length > 0
            ? 'border-sky-500 bg-sky-500/20 text-sky-200'
            : 'border-slate-700 bg-slate-950 text-slate-400 hover:border-slate-600'
        }`}
      >
        <Globe2 className="h-3 w-3" /> Translate ({summary})
      </button>
      {open && (
        <div className="absolute left-0 top-full z-40 mt-1 w-72 rounded border border-slate-800 bg-slate-950 p-2 shadow-2xl" data-testid="translation-menu">
          <div className="mb-2 flex items-center justify-between">
            <span className="text-[10px] uppercase tracking-wider text-slate-500">
              Broadcast translations
            </span>
            <button type="button" onClick={() => setOpen(false)}
                    className="text-slate-500 hover:text-slate-200">
              <X className="h-3 w-3" />
            </button>
          </div>
          <div className="max-h-64 overflow-y-auto grid grid-cols-2 gap-0.5 text-xs">
            {languages.map((l) => (
              <label key={l.code}
                     className={`flex items-center gap-1 rounded px-1.5 py-1 ${
                       selected.has(l.code) ? 'bg-sky-500/10 text-sky-200' : 'text-slate-300 hover:bg-slate-800/60'
                     }`}>
                <input
                  type="checkbox"
                  data-testid={`translate-lang-${l.code}`}
                  checked={selected.has(l.code)}
                  onChange={() => toggle(l.code)}
                  className="h-3 w-3"
                />
                <span className="text-[10px] uppercase tracking-wider text-slate-500">{l.code}</span>
                <span className="truncate">{l.label}</span>
              </label>
            ))}
          </div>
          <p className="mt-1 text-[9px] text-slate-500">
            Uses OpenNMT if configured, else Claude via Emergent LLM key.
          </p>
        </div>
      )}
    </div>
  );
}
